import React, { useEffect, useRef } from 'react'
import queryString from 'query-string'
import { connect } from "react-redux"
import { getCookie } from 'cookie'
import { setDoneAction } from 'actions/intercomActions'
import sbjs from 'sourcebuster'

sbjs.init()

// Custom Intercom component to replace react-intercom (which uses deprecated lifecycle methods)
const INTERCOM_APP_ID = 'c8oesru0'

const IntercomWidget = ({ appID, ...settings }) => {
  const settingsRef = useRef(settings)

  useEffect(() => {
    // Initialize Intercom
    if (typeof window !== 'undefined') {
      window.intercomSettings = {
        app_id: appID,
        ...settings
      }

      // Load Intercom script if not already loaded
      if (!window.Intercom) {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.async = true
        script.src = `https://widget.intercom.io/widget/${appID}`
        script.onload = () => {
          if (window.Intercom) {
            window.Intercom('boot', { app_id: appID, ...settings })
          }
        }
        document.body.appendChild(script)
      } else {
        window.Intercom('boot', { app_id: appID, ...settings })
      }
    }

    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown')
      }
    }
  }, [appID])

  // Update Intercom when settings change
  useEffect(() => {
    if (window.Intercom && JSON.stringify(settings) !== JSON.stringify(settingsRef.current)) {
      settingsRef.current = settings
      window.Intercom('update', settings)
    }
  }, [settings])

  return null
}

@connect((store) => {
	return {
    ...store.intercom,
    accounts: store.root.accounts,
    user: store.root.user,
    applicationId: store.root.applicationId,
    view: queryString.parse(location.search).view,
    ...(store.plan.subscription ? {
      plan: (store.plan.subscription.plans || [])[0] ,
      subscription_status: store.plan.subscription.status,
      trial_end: store.plan.subscription.trial_end,
    } : {}),
    monthlySales: store.plan.monthlySales,
	}
}, { setDoneAction })

export default class IntercomCurl extends React.Component {
  state = {
    mimic: true,
    pendingUpdate: false
  }

  /**
   * Get intercom settings from props
   * @param {Object} nextProps nextProps while updating
   */
  propsToSettings = (nextProps) => {
    const { 
      accounts = [], user, applicationId, view, lastLoginDate, 
      plan, subscription_status, trial_end, monthlySales, doneAction, 
      upgradeModalOpen, upgradeDismissed,
    } = (nextProps || this.props)
    const accountWithName = accounts.filter(account => account.given_name && account.family_name)[0]
    const name = accountWithName ? `${accountWithName.given_name} ${accountWithName.family_name}` : ''
    const currentPlan = plan ? `${plan.product} ${plan.nickname}` : null
    const trialEnd = trial_end ? (new Date(trial_end)).toString() : null
    const {
      typ: website_utm_type,
      src: website_utm_source,
      mdm: website_utm_medium,
      cmp: website_utm_campaign,
      cnt: website_utm_content,
      trm: website_utm_term,
    } = (sbjs.get || {})?.current || {}
    const settings = {
      name,
      email: user.email || user.owner || (accounts[0] || {}).email,
      user_id: user.id,
      merchant_id: accounts.map(account => account.merchant_id).join('|'),
      last_visited_app: applicationId,
      last_visited_view: view || 'default',
      // #693: hide the launcher during onboarding — the bubble/message overlaps
      // the wizard's Next button. requires_onboarding is the redux-reactive signal
      // (true through the wizard, flips false at finish()), so this updates cleanly.
      hide_default_launcher: applicationId === 'ai' || !!user?.requires_onboarding,
      last_login_date: lastLoginDate,
      [`${applicationId}_visited`]: true,
      current_plan: currentPlan,
      subscription_status: subscription_status || null,
      trial_end: trialEnd,
      monthly_sales: monthlySales,
      upgrade_modal_open: upgradeModalOpen,
      upgrade_dismissed: upgradeDismissed,
      ...this.actionProps(doneAction),
      ...this.companyProps(accounts, currentPlan, trialEnd, monthlySales, user),
      ...(website_utm_type && {
        website_utm_type,
        website_utm_source,
        website_utm_medium,
        website_utm_campaign,
        website_utm_content,
        website_utm_term, 
      }),
    }
    return settings
  }

  /**
   * Convert linked accounts to company props in intercom settings
   * @param {Array} accounts linked accounts from API
   */
  companyProps = (accounts, currentPlan, trialEnd, monthlySales, user) => {
    if (!accounts.length) {
      return null
    }
    const companyId = accounts[0].owner_id
    const companyName = accounts.map(a => a.merchant_id).join('|')
    const companyWebsites = accounts.map(a => a.website).filter(v => v).join('|')
    const company = {
      company: {
        id: companyId,
        name: companyName,
        plan: currentPlan,
        trial_end: trialEnd,
        monthly_sales: monthlySales,
        website: companyWebsites,
      }
    }
    if (user.billing && user.billing.id) {
      company.company.stripe_id = user.billing.id
    }
    return company
  }

  /**
   * Convert done action object to action props in intercom settings
   * @param {Object} doneAction done action dispatched by different apps
   */
  actionProps = (doneAction) => {
    if (!window.intercomSettings || !doneAction) return {}
    console.log('doneAction', doneAction)
    if (doneAction) {
      switch(doneAction.type) {
        case 'new_plan':
          return {
            last_plan_action: `${doneAction.oldPlan?.product || 'None'} ${doneAction.oldPlan?.interval || 'None'}|${doneAction.product} ${doneAction.interval}|${doneAction.date}`
          }
        case 'new_account':
          return {
            last_account_action: `new account|${doneAction.merchant_id}|${doneAction.platform}|${doneAction.date}`
          }
        case 'required_action':
          return {
            last_required_action: doneAction.details
          }
        case 'upgrade_dimissed':
          return {
            upgrade_dismissed: (new Date()).toISOString()
          }
        case 'add_on_upgrade_modal_seen':
          return {
            add_on_upgrade_modal_seen: doneAction.addOn
          }
        default:
          return {}
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Handle doneAction updates
    if (this.props.doneAction && this.props.doneAction !== prevProps.doneAction) {
      if (window.Intercom) {
        window.Intercom('update', this.propsToSettings())
      }
      this.props.setDoneAction(null)
    }
  }

  componentDidMount() {
    const admin_token = getCookie('admin_token')
    const app_token = getCookie('app_token')
    if (!admin_token || (admin_token === app_token)) {
      this.setState({ mimic: false })
    }
  }

  render() {
    if ((!this.props.accounts || !this.props.accounts.length) && !this.props.user) return null
    if (this.state.mimic) return null

    return (
      <IntercomWidget
        appID={INTERCOM_APP_ID}
        {...this.propsToSettings()}
      />
    )
  }

}
